import { Product } from "store/product/ProductTypes";
import {
  LoadTopCategoriesResponseAction,
  LoadChildCategoriesResponseAction,
  Translations,
} from "../category/CategoryTypes";
import {
  FETCH_FIND_PRODUCTS_TOP_CATEGORIES,
  FETCH_FIND_PRODUCTS_CHILD_CATEGORIES,
  RESET_FIND_PRODUCTS,
  ChildCategory,
  ProductNodes,
  RESET_FIND_PRODUCTS_PRODUCTS_LIST,
  FETCH_PRODUCTS_FOR_FIND_PRODUCTS_CATEGORY,
  LoadProductsFromCategoryForFindProductsCategoryResponse,
  FETCH_SEARCH_PRODUCTS_FOR_FIND_PRODUCTS,
  SearchProductsForFindProductsResponse,
  UPDATE_PRODUCTS_CATEGORY,
  FETCH_PRODUCT_IDS_FOR_FIND_PRODUCTS_CATEGORY,
  FETCH_PRODUCTS_FOR_FIND_PRODUCTS,
  FETCH_FIND_PRODUCTS_COLORS,
  LoadProductsFromCategoryResponseAction,
  QUERY_PRODUCT_FOR_FIND_PRODUCTS,
  QueryProductForFindProductsResponse,
  RESET_QUERY_PRODUCT_FOR_FIND_PRODUCTS,
  UPDATE_FIND_PRODUCTS_PROGRESS_CACHE,
} from "../find-products/FindProductsTypes";
import {
  ProductSequenceData,
  productColorsData,
  REFRESH_PRODUCT,
  REFRESH_PRODUCT_COLOR,
  CategoryToProductData,
} from "../product-list/ProductListTypes";
import { ProductColorNode } from "../product/ProductReducers";
import AppEnv from "../../utils/AppEnv";

const PRODUCT_DETAILS_PAGESIZE = +(AppEnv.PRODUCT_DETAILS_PAGESIZE ?? 4);

export interface FindProductsState {
  topCategory: {
    categoryId: string;
    childCategoryCount: number;
    translations: Translations[];
    productCount?: number;
    isProductCountLoading?: boolean;
    code: string;
    name: string;
    isPublished: boolean;
  }[];
  childCategory: ChildCategory;
  products: ProductNodes[];
  isSubmitting: boolean;
  isSubmitted: boolean;
  productList: ProductSequenceData[];
  productIds: string[];
  isFetchingProductIds: boolean;
  isProductIdsFetched: boolean;
  allPagesFetched?: boolean;
  addingRemovingProductIdsList?: string[];
  productById: Record<string, Product>;
  /** Color swatch data for find-products modal only (not `product.productsColorData`). */
  productsColorData: ProductColorNode;
  /** Monotonic cache for loading % (see `selectFindProductsLoadingPercentage`). */
  progressCache: Record<string, number>;
  /** Count of query API pages received (search); resets on new query. */
  queryPagesCompleted: number;
}

const initialState: FindProductsState = {
  topCategory: [],
  childCategory: {},
  products: [],
  isSubmitting: false,
  isSubmitted: false,
  productList: [],
  productIds: [],
  isFetchingProductIds: false,
  isProductIdsFetched: false,
  allPagesFetched: false,
  addingRemovingProductIdsList: [],
  productById: {},
  productsColorData: {},
  progressCache: {},
  queryPagesCompleted: 0,
};

const mergeFindProductsColorsFromResponse = (
  productColorsData: productColorsData[],
): ProductColorNode => {
  const productColorObjects: ProductColorNode = {};
  productColorsData?.forEach((productColorObj) => {
    if (Object.keys(productColorObj).length) {
      const product = {
        productId: productColorObj.productId,
        hasManageableColors:
          productColorObj.cachedProduct.product.hasManageableColors,
        hasSequenceableColors:
          productColorObj.cachedProduct.product.hasSequenceableColors,
        colorVariationAttributeId:
          productColorObj.cachedProduct.product.colorVariationAttributeId ?? "",
        colors: productColorObj.cachedProduct.product.colors,
        cachedAt: productColorObj.cachedProduct.cachedAt,
        categorySpecific:
          productColorObj?.cachedProduct?.product?.categorySpecific ?? [],
        productColorDetailsLoaded: true,
        productColorObj,
      };
      productColorObjects[productColorObj.productId] = product;
    }
  });
  return productColorObjects;
};

const getProductsFromQueryResponse = (
  action: QueryProductForFindProductsResponse,
): [ProductSequenceData[], Record<string, Product>] => {
  const productsInCategory: ProductSequenceData[] = [];
  const productByIdData: Record<string, Product> = {};
  if (action?.payload) {
    for (const item of action.payload?.results || []) {
      const product = item.product;
      productsInCategory.push({
        productId: product.productId,
      });
      productByIdData[product.productId] = product;
    }
  }
  return [productsInCategory, productByIdData];
};

const getProductsFromResponse = (
  action: LoadProductsFromCategoryResponseAction,
): [ProductSequenceData[], Record<string, Product>] => {
  const productsInCategory: ProductSequenceData[] = [];
  const productById: Record<string, Product> = {};
  if (action?.payload) {
    for (const v of action.payload.results) {
      productsInCategory.push({
        sequence: v?.cachedProduct?.sequence,
        productId: v?.productId,
      });
      productById[v?.productId] = v?.cachedProduct?.product;
    }
  }
  return [productsInCategory, productById];
};

export const FindProductsReducer = (state = initialState, action: any) => {
  switch (action.type) {
    case FETCH_FIND_PRODUCTS_TOP_CATEGORIES.SUCCESS:
      const topCategoriesActions = action as LoadTopCategoriesResponseAction;
      return {
        ...state,
        topCategory: topCategoriesActions.payload.results.map((cat) => ({
          categoryId: cat.categoryId,
          childCategoryCount: cat.childCategoryCount,
          code: cat.code,
          name: cat.name,
          productCount: cat.productCount,
          translations: cat.translations,
          isPublished: cat.isPublished,
        })),
      };
    case FETCH_FIND_PRODUCTS_CHILD_CATEGORIES.SUCCESS:
      const childCategoriesAction = action as LoadChildCategoriesResponseAction;
      return {
        ...state,
        childCategory: {
          ...state.childCategory,
          [childCategoriesAction.payload.results[0].parentId]: {
            childCategories: childCategoriesAction.payload.results.map(
              (node) => ({
                categoryId: node.categoryId,
                childCategoryCount: node.childCategoryCount,
                code: node.code,
                name: node.name,
                productCount: node.productCount,
                translations: node.translations,
                isPublished: node.isPublished,
              }),
            ),
          },
        },
      };
    case RESET_FIND_PRODUCTS:
      return initialState;
    case RESET_FIND_PRODUCTS_PRODUCTS_LIST:
      return {
        ...state,
        products: [],
      };
    case FETCH_PRODUCTS_FOR_FIND_PRODUCTS_CATEGORY.SUCCESS:
      const loadProductsAction =
        action as LoadProductsFromCategoryForFindProductsCategoryResponse;
      const products = loadProductsAction.payload.data.CategoryToProduct;
      return {
        ...state,
        products,
      };
    case FETCH_SEARCH_PRODUCTS_FOR_FIND_PRODUCTS.SUCCESS:
      const searchProductsAction =
        action as SearchProductsForFindProductsResponse;
      const productList = searchProductsAction.payload.data.Product;
      return {
        ...state,
        products: [...productList],
      };
    case UPDATE_PRODUCTS_CATEGORY.REQUEST:
      return {
        ...state,
        isSubmitting: true,
        isSubmitted: false,
      };
    case UPDATE_PRODUCTS_CATEGORY.SUCCESS:
      return {
        ...state,
        isSubmitting: false,
        isSubmitted: true,
      };
    case FETCH_PRODUCT_IDS_FOR_FIND_PRODUCTS_CATEGORY.REQUEST: {
      const payload = action.payload;
      return {
        ...state,
        productIds: payload.productIds ? state.productIds : [],
        isFetchingProductIds: payload.productIds ? false : true,
        productList: [],
        productsColorData: {},
        isProductIdsFetched: false,
        allPagesFetched: false,
        addingRemovingProductIdsList: [],
        progressCache: {},
        queryPagesCompleted: 0,
      };
    }
    case FETCH_PRODUCT_IDS_FOR_FIND_PRODUCTS_CATEGORY.SUCCESS: {
      const productIdsData = action.payload.productIds;
      return {
        ...state,
        productIds: productIdsData,
        isProductIdsFetched: true,
        isFetchingProductIds: false,
        allPagesFetched: productIdsData.length ? state.allPagesFetched : true,
      };
    }
    case FETCH_PRODUCT_IDS_FOR_FIND_PRODUCTS_CATEGORY.FAILURE: {
      return {
        ...state,
        isProductIdsFetched: false,
        isFetchingProductIds: false,
        productList: [],
        allPagesFetched: true,
      };
    }
    case FETCH_PRODUCTS_FOR_FIND_PRODUCTS.SUCCESS: {
      const fetchAction = action as LoadProductsFromCategoryResponseAction;
      const pagesFetched = action?.payload?.totalPagesFetched;
      // Search: QUERY_PRODUCT (incl. multi-page `next`) owns `allPagesFetched`; cache chunks only enrich.
      // Category: chunks append; `totalPagesFetched` vs id count drives `allPagesFetched`.
      const skipFindProductsPagination =
        action?.payload?.skipFindProductsPagination === true;

      const [productListData, productById] =
        getProductsFromResponse(fetchAction);

      const fetchMap = new Map(
        productListData.map((item) => [item.productId, item]),
      );
      const nextProductList = skipFindProductsPagination
        ? state.productList.map((item) => fetchMap.get(item.productId) ?? item)
        : [...state.productList, ...productListData];

      return {
        ...state,
        productList: nextProductList,
        allPagesFetched: skipFindProductsPagination
          ? state.allPagesFetched
          : pagesFetched &&
              pagesFetched ===
                Math.ceil(state.productIds.length / PRODUCT_DETAILS_PAGESIZE)
            ? true
            : false,
        productById: {
          ...state.productById,
          ...productById,
        },
      };
    }
    case FETCH_PRODUCTS_FOR_FIND_PRODUCTS.FAILURE: {
      const failPagesFetched = action?.payload?.totalPagesFetched;
      const failSkip = action?.payload?.skipFindProductsPagination === true;
      if (failSkip) {
        return { ...state };
      }
      return {
        ...state,
        allPagesFetched:
          failPagesFetched &&
          failPagesFetched ===
            Math.ceil(state.productIds.length / PRODUCT_DETAILS_PAGESIZE)
            ? true
            : false,
      };
    }
    case FETCH_FIND_PRODUCTS_COLORS.SUCCESS: {
      const productColorsList = action.payload?.results;
      const validProductColors = productColorsList?.filter(
        (item: productColorsData & { errorId?: string }) =>
          item.cachedProduct && !item.errorId,
      );
      const productsColorDataFromResponse = mergeFindProductsColorsFromResponse(
        validProductColors ?? [],
      );
      return {
        ...state,
        productsColorData: {
          ...state.productsColorData,
          ...productsColorDataFromResponse,
        },
      };
    }
    case FETCH_FIND_PRODUCTS_COLORS.FAILURE: {
      return { ...state };
    }
    case QUERY_PRODUCT_FOR_FIND_PRODUCTS.REQUEST:
      // Only reset on the first request (when there's no next token)
      if (!action.payload?.next) {
        return {
          ...state,
          productIds: [],
          productList: [],
          productsColorData: {},
          isFetchingProductIds: true,
          isProductIdsFetched: false,
          allPagesFetched: false,
          addingRemovingProductIdsList: [],
          progressCache: {},
          queryPagesCompleted: 0,
        };
      }
      // For subsequent pages, keep accumulating
      return {
        ...state,
        isFetchingProductIds: true,
      };
    case QUERY_PRODUCT_FOR_FIND_PRODUCTS.SUCCESS:
      const queryAction = action as QueryProductForFindProductsResponse;
      const [productsInCategory, productByIdData] =
        getProductsFromQueryResponse(queryAction);
      // Extract productIds from current page results
      const pageProductIds = (queryAction.payload?.results || [])
        .map((item: any) => item.product?.productId)
        .filter((id: string | undefined) => id !== undefined);

      // Check if there are more pages (next token exists)
      const hasMorePages = !!queryAction.payload?.next;

      return {
        ...state,
        // Accumulate productIds from all pages
        productIds: [...state.productIds, ...pageProductIds],
        // Accumulate productList from all pages
        productList: [...state.productList, ...productsInCategory],
        // Merge productById data
        productById: {
          ...state.productById,
          ...productByIdData,
        },
        isFetchingProductIds: hasMorePages, // Keep fetching if more pages exist
        isProductIdsFetched: !hasMorePages, // Mark as fetched only when no more pages
        allPagesFetched: !hasMorePages, // Mark as complete only when no more pages
        queryPagesCompleted: state.queryPagesCompleted + 1,
      };
    case QUERY_PRODUCT_FOR_FIND_PRODUCTS.FAILURE:
      return {
        ...state,
        isFetchingProductIds: false,
        isProductIdsFetched: false,
        allPagesFetched: true,
        queryPagesCompleted: 0,
        progressCache: {},
      };
    case RESET_QUERY_PRODUCT_FOR_FIND_PRODUCTS:
      return {
        ...state,
        productIds: [],
        productList: [],
        productById: {},
        productsColorData: {},
        progressCache: {},
        queryPagesCompleted: 0,
      };
    case UPDATE_FIND_PRODUCTS_PROGRESS_CACHE: {
      const { progress } = action.payload;
      const cacheKey = "find-products";
      const currentProgress = state.progressCache[cacheKey] || 0;
      const newProgress = Math.max(currentProgress, progress);
      return {
        ...state,
        progressCache: {
          ...state.progressCache,
          [cacheKey]: newProgress,
        },
      };
    }
    case REFRESH_PRODUCT.SUCCESS: {
      const responseData = action.payload as CategoryToProductData;
      const pid = responseData?.productId;
      const next = responseData?.cachedProduct?.product;
      if (!pid || !next || !state.productById[pid]) {
        return state;
      }
      return {
        ...state,
        productById: {
          ...state.productById,
          [pid]: {
            ...state.productById[pid],
            ...next,
          },
        },
      };
    }
    case REFRESH_PRODUCT_COLOR.SUCCESS: {
      const item = action.payload as productColorsData & { errorId?: string };
      if (!item?.cachedProduct || item.errorId) {
        return state;
      }
      const merged = mergeFindProductsColorsFromResponse([item]);
      if (!Object.keys(merged).length) {
        return state;
      }
      return {
        ...state,
        productsColorData: {
          ...state.productsColorData,
          ...merged,
        },
      };
    }
    default:
      return state;
  }
};
